// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect, useCallback } from "react";
import {
  TrendingUp,
  TrendingDown,
  Target,
  Activity,
  AlertCircle,
  Calendar,
  Percent,
  BarChart3,
  Loader2,
  Mail,
} from "lucide-react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
} from "recharts";
import { fetchAllForecastData } from "../../services/financialReportingService";
import useTenants from "../../hooks/useTenants";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import PageLoader from "../../components/PageLoader";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess } from "../../config/roleConfig";
import { getAdminTenantConfig } from "../../utils/tenantConfig";
import { useTheme } from "../../context/ThemeContext";
import useTenantBranding from "../../hooks/useTenantBranding";

const FinancialForecast = () => {
  const { isDarkMode } = useTheme();
  const { primaryColor } = useTenantBranding();
  const [forecastData, setForecastData] = useState([]);
  const [parameters, setParameters] = useState(null);
  const [projections, setProjections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeScenarios, setActiveScenarios] = useState({
    base: true,
    conservative: true,
    optimistic: true,
    historical: true,
  });
  const { userRoles } = useRoutes();
  const isAdminUser = hasSuperAdminAccess(userRoles || []);

  const {
    selectedTenant,
    setSelectedTenant,
    loading: tenantsLoading,
    tenantOptions,
    isAllTenantsSelected,
  } = useTenants(getAdminTenantConfig(isAdminUser));

  const fetchForecastData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const isAllSelected =
        isAdminUser && typeof isAllTenantsSelected === "function"
          ? isAllTenantsSelected()
          : false;
      const tenantId = isAdminUser
        ? isAllSelected
          ? null
          : selectedTenant
        : null;

      const data = await fetchAllForecastData(tenantId);

      // Transform forecast data
      const transformedForecastData = [];

      // Add historical data from seasonality factors (only months with data)
      data.seasonalityFactors.forEach((month) => {
        if (month.historical_avg_revenue > 0) {
          transformedForecastData.push({
            month: `${month.month_name} 2024`,
            actual: month.historical_avg_revenue,
            type: "historical",
          });
        }
      });

      // Add forecast data
      data.forecastData.forecast_periods.forEach((period) => {
        if (
          period.base_scenario > 0 ||
          period.conservative_scenario > 0 ||
          period.optimistic_scenario > 0
        ) {
          transformedForecastData.push({
            month: `${period.month_name} 2025`,
            type: "forecast",
            base: period.base_scenario,
            conservative: period.conservative_scenario,
            optimistic: period.optimistic_scenario,
            confidence_lower: period.confidence_interval_lower,
            confidence_upper: period.confidence_interval_upper,
          });
        }
      });

      // Transform parameters
      const transformedParameters = {
        revenue_growth: data.forecastData.forecast_parameters.base_growth_rate,
        booking_growth:
          data.forecastData.forecast_parameters.booking_growth_rate,
        seasonality_enabled: true,
        confidence_interval: Math.round(
          data.forecastData.forecast_parameters.confidence_interval * 100,
        ),
        forecast_horizon:
          data.forecastData.forecast_parameters.forecast_horizon_months,
        collection_rate_base: Math.round(
          data.forecastData.forecast_parameters.collection_rate_base * 100,
        ),
        collection_rate_conservative: Math.round(
          data.forecastData.forecast_parameters.collection_rate_conservative *
            100,
        ),
        collection_rate_optimistic: Math.round(
          data.forecastData.forecast_parameters.collection_rate_optimistic *
            100,
        ),
      };

      // Transform projections
      const transformedProjections = {
        base: {
          annual_revenue: data.scenarioAnalysis[0]?.projected_revenue || 0,
          annual_profit: data.scenarioAnalysis[0]?.projected_profit || 0,
          profit_margin:
            data.scenarioAnalysis[0]?.projected_revenue > 0
              ? Math.round(
                  (data.scenarioAnalysis[0].projected_profit /
                    data.scenarioAnalysis[0].projected_revenue) *
                    100 *
                    10,
                ) / 10
              : 0,
          total_bookings: data.scenarioAnalysis[0]?.projected_bookings || 0,
        },
        conservative: {
          annual_revenue: data.scenarioAnalysis[1]?.projected_revenue || 0,
          annual_profit: data.scenarioAnalysis[1]?.projected_profit || 0,
          profit_margin:
            data.scenarioAnalysis[1]?.projected_revenue > 0
              ? Math.round(
                  (data.scenarioAnalysis[1].projected_profit /
                    data.scenarioAnalysis[1].projected_revenue) *
                    100 *
                    10,
                ) / 10
              : 0,
          total_bookings: data.scenarioAnalysis[1]?.projected_bookings || 0,
        },
        optimistic: {
          annual_revenue: data.scenarioAnalysis[2]?.projected_revenue || 0,
          annual_profit: data.scenarioAnalysis[2]?.projected_profit || 0,
          profit_margin:
            data.scenarioAnalysis[2]?.projected_revenue > 0
              ? Math.round(
                  (data.scenarioAnalysis[2].projected_profit /
                    data.scenarioAnalysis[2].projected_revenue) *
                    100 *
                    10,
                ) / 10
              : 0,
          total_bookings: data.scenarioAnalysis[2]?.projected_bookings || 0,
        },
      };

      setForecastData(transformedForecastData);
      setParameters(transformedParameters);
      setProjections(transformedProjections);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching forecast data:", error);
      setError("Failed to load forecast data. Please try again.");
      setLoading(false);
    }
  }, [selectedTenant, isAllTenantsSelected, isAdminUser]);

  useEffect(() => {
    fetchForecastData();
  }, [selectedTenant, fetchForecastData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("de-DE").format(value);
  };

  const toggleScenario = (scenario) => {
    setActiveScenarios((prev) => ({
      ...prev,
      [scenario]: !prev[scenario],
    }));
  };

  const handleRefresh = () => {
    fetchForecastData();
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-xl border border-gray-200 dark:border-slate-600">
          <p className="font-semibold text-gray-900 dark:text-slate-100 mb-2">
            {label}
          </p>
          {payload.map((entry, index) => {
            if (entry.value) {
              return (
                <p
                  key={index}
                  className="text-sm flex items-center gap-2"
                  style={{ color: entry.color }}
                >
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  {entry.name}: {formatCurrency(entry.value)}
                </p>
              );
            }
            return null;
          })}
        </div>
      );
    }
    return null;
  };

  const ScenarioCard = ({ title, data, color, icon: Icon }) => {
    return (
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 border-l-4 dark:border-slate-700"
        style={{ borderLeftColor: color }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundColor: `${color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
            {title}
          </h3>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-slate-400">
              Jahresumsatz
            </span>
            <span className="text-lg font-bold text-gray-900 dark:text-slate-100">
              {formatCurrency(data.annual_revenue)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-slate-400">
              Jahresgewinn
            </span>
            <span className="text-lg font-bold text-gray-900 dark:text-slate-100">
              {formatCurrency(data.annual_profit)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-slate-400">
              Gewinnmarge
            </span>
            <span className="text-lg font-bold" style={{ color }}>
              {data.profit_margin}%
            </span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t dark:border-slate-600">
            <span className="text-sm text-gray-600 dark:text-slate-400">
              Buchungen
            </span>
            <span className="text-base font-semibold text-gray-900 dark:text-slate-100">
              {formatNumber(data.total_bookings)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const ParameterCard = ({ icon: Icon, title, value, subtitle, color }) => {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-4 dark:border dark:border-slate-700">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">
              {title}
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              {value}
            </p>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title="Financial Forecast"
        subtitle="AI-powered financial forecasting and predictive analytics"
        tenantOptions={isAdminUser ? tenantOptions : []}
        selectedTenant={isAdminUser ? selectedTenant : null}
        onTenantChange={isAdminUser ? setSelectedTenant : undefined}
        tenantsLoading={isAdminUser ? tenantsLoading : false}
        buttons={[
          {
            text: "Refresh",
            icon: <Loader2 className="h-4 w-4" />,
            onClick: handleRefresh,
            className:
              "bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 flex items-center gap-2",
          },
          {
            text: "Export Report",
            icon: <Mail className="h-4 w-4" />,
            // TODO: wire up export — no backend endpoint yet
            onClick: () => {},
            className:
              "bg-primary-color text-white px-4 py-2 rounded-md hover:opacity-90 flex items-center gap-2",
          },
        ]}
      />

      {loading && <PageLoader message="Lade Prognosedaten..." />}

      {/* Error State */}
      {error && !loading && (
        <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
          <div className="text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">
              Error Loading Forecast Data
            </h2>
            <p className="text-gray-600 dark:text-slate-400 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="bg-primary-color text-white px-4 py-2 rounded-md hover:opacity-90 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <div className="mx-auto px-4 lg:px-6 py-6 space-y-6">
          {/* Forecast Parameters */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-4">
              Prognose-Parameter
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <ParameterCard
                icon={TrendingUp}
                title="Umsatzwachstum"
                value={`+${parameters.revenue_growth}%`}
                subtitle="Durchschnittlich"
                color="bg-primary-color"
              />
              <ParameterCard
                icon={BarChart3}
                title="Buchungswachstum"
                value={`+${parameters.booking_growth}%`}
                subtitle="Durchschnittlich"
                color="bg-primary-color"
              />
              <ParameterCard
                icon={Activity}
                title="Konfidenzintervall"
                value={`±${parameters.confidence_interval}%`}
                subtitle="Unsicherheitsbereich"
                color="bg-primary-color"
              />
              <ParameterCard
                icon={Calendar}
                title="Prognosehorizont"
                value={`${parameters.forecast_horizon} Monate`}
                subtitle="Saisonalität aktiviert"
                color="bg-primary-color"
              />
            </div>
          </div>

          {/* Forecast Chart */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 mb-8 dark:border dark:border-slate-700">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-4 md:mb-0">
                Umsatzprognose & Szenarien
              </h2>

              {/* Scenario Toggle Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => toggleScenario("historical")}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    activeScenarios.historical
                      ? "bg-gray-700 dark:bg-slate-600 text-white shadow-md"
                      : "bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600"
                  }`}
                >
                  Historisch
                </button>
                <button
                  onClick={() => toggleScenario("base")}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    activeScenarios.base
                      ? "bg-primary-color text-white shadow-md"
                      : "bg-primary-color-tint primary-color hover:bg-primary-color-tint hover:opacity-90"
                  }`}
                >
                  Basis
                </button>
                <button
                  onClick={() => toggleScenario("conservative")}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    activeScenarios.conservative
                      ? "bg-green-500 text-white shadow-md"
                      : "bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50"
                  }`}
                >
                  Konservativ
                </button>
                <button
                  onClick={() => toggleScenario("optimistic")}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    activeScenarios.optimistic
                      ? "bg-orange-500 text-white shadow-md"
                      : "bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/50"
                  }`}
                >
                  Optimistisch
                </button>
              </div>
            </div>

            {forecastData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={forecastData}>
                  <defs>
                    <linearGradient
                      id="confidenceArea"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={primaryColor || "#3b82f6"}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={primaryColor || "#3b82f6"}
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isDarkMode ? "#475569" : "#e5e7eb"}
                  />
                  <XAxis
                    dataKey="month"
                    stroke={isDarkMode ? "#94a3b8" : "#6b7280"}
                    tick={{ fill: isDarkMode ? "#94a3b8" : "#6b7280" }}
                    style={{ fontSize: "12px" }}
                  />
                  <YAxis
                    stroke={isDarkMode ? "#94a3b8" : "#6b7280"}
                    tick={{ fill: isDarkMode ? "#94a3b8" : "#6b7280" }}
                    style={{ fontSize: "12px" }}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{
                      fontSize: "14px",
                      paddingTop: "20px",
                      color: isDarkMode ? "#94a3b8" : undefined,
                    }}
                    iconType="circle"
                  />

                  {/* Confidence Interval */}
                  {activeScenarios.base && (
                    <Area
                      type="monotone"
                      dataKey="confidence_upper"
                      stroke="none"
                      fill="url(#confidenceArea)"
                      name="Konfidenzbereich"
                    />
                  )}
                  {activeScenarios.base && (
                    <Area
                      type="monotone"
                      dataKey="confidence_lower"
                      stroke="none"
                      fill="url(#confidenceArea)"
                    />
                  )}

                  {/* Historical Data */}
                  {activeScenarios.historical && (
                    <Bar
                      dataKey="actual"
                      fill="#4b5563"
                      name="Ist-Umsatz"
                      radius={[8, 8, 0, 0]}
                    />
                  )}

                  {/* Forecast Scenarios */}
                  {activeScenarios.conservative && (
                    <Line
                      type="monotone"
                      dataKey="conservative"
                      stroke="#10b981"
                      strokeWidth={2.5}
                      dot={{ fill: "#10b981", r: 5 }}
                      name="Konservativ"
                    />
                  )}

                  {activeScenarios.base && (
                    <Line
                      type="monotone"
                      dataKey="base"
                      stroke={primaryColor || "#3b82f6"}
                      strokeWidth={3}
                      dot={{ fill: primaryColor || "#3b82f6", r: 6 }}
                      name="Basis"
                    />
                  )}

                  {activeScenarios.optimistic && (
                    <Line
                      type="monotone"
                      dataKey="optimistic"
                      stroke="#f97316"
                      strokeWidth={2.5}
                      dot={{ fill: "#f97316", r: 5 }}
                      name="Optimistisch"
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-80 text-gray-500 dark:text-slate-400">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-slate-500" />
                  <p className="text-lg font-medium">
                    Keine Prognosedaten verfügbar
                  </p>
                  <p className="text-sm">
                    Daten werden angezeigt, sobald ausreichend historische Daten
                    vorhanden sind.
                  </p>
                </div>
              </div>
            )}

            {/* Chart Legend Info */}
            <div className="mt-6 p-4 bg-gradient-primary-secondary-tint rounded-lg border border-primary-color border-opacity-30">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 primary-color mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-900 dark:text-slate-100">
                  <p className="font-semibold mb-1">Methodik der Prognose</p>
                  <p className="primary-color">
                    Die Prognosen basieren auf historischen Daten mit saisonaler
                    Analyse. Der Basis-Fall verwendet{" "}
                    {parameters?.revenue_growth}% Wachstum mit{" "}
                    {parameters?.collection_rate_base}% Inkassoquote.
                    Konservativ: {parameters?.collection_rate_conservative}%
                    Inkassoquote. Optimistisch:{" "}
                    {parameters?.collection_rate_optimistic}% Inkassoquote.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Projected Annual Performance */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-4">
              Prognostizierte Jahresleistung
            </h2>
            {projections &&
            (projections.base.annual_revenue > 0 ||
              projections.conservative.annual_revenue > 0 ||
              projections.optimistic.annual_revenue > 0) ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <ScenarioCard
                  title="Basis-Szenario"
                  data={projections.base}
                  color={primaryColor || "#3b82f6"}
                  icon={Target}
                />
                <ScenarioCard
                  title="Konservatives Szenario"
                  data={projections.conservative}
                  color="#10b981"
                  icon={TrendingDown}
                />
                <ScenarioCard
                  title="Optimistisches Szenario"
                  data={projections.optimistic}
                  color="#f97316"
                  icon={TrendingUp}
                />
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-8 text-center dark:border dark:border-slate-700">
                <Target className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-slate-500" />
                <p className="text-lg font-medium text-gray-600 dark:text-slate-400 mb-2">
                  Keine Szenario-Prognosen verfügbar
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-500">
                  Szenario-Analysen werden angezeigt, sobald ausreichend Daten
                  für Prognosen vorhanden sind.
                </p>
              </div>
            )}
          </div>

          {/* Collection Rates */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 dark:border dark:border-slate-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-4">
              Inkassoquoten nach Szenario
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gradient-primary-secondary-tint rounded-lg border border-primary-color border-opacity-30">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-slate-200">
                    Basis
                  </span>
                  <Percent className="w-4 h-4 primary-color" />
                </div>
                <p className="text-3xl font-bold primary-color mt-2">
                  {parameters.collection_rate_base}%
                </p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg dark:border dark:border-green-800/50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-900 dark:text-green-200">
                    Konservativ
                  </span>
                  <Percent className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                  {parameters.collection_rate_conservative}%
                </p>
              </div>
              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg dark:border dark:border-orange-800/50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-orange-900 dark:text-orange-200">
                    Optimistisch
                  </span>
                  <Percent className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                </div>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400 mt-2">
                  {parameters.collection_rate_optimistic}%
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialForecast;
